import clsx from 'clsx';
import { Tabs, type TabItem } from '../../ui/v2';
import type {
  AdminTab,
  IntegrationSubTab,
  NotificationSubTab,
  OnboardingBillingSubtab,
  PlansSubTab,
  PosConfigSubtab,
  TwilioConfigSubtab,
} from './platformAdminRoutes';

const MODEL_TAB_KEYS = [
  'store',
  'storeGroups',
  'user',
  'assistant',
  'adminOrders',
  'adminCalls',
  'adminReservations',
  'edit',
] as const satisfies readonly AdminTab[];

type ModelTab = (typeof MODEL_TAB_KEYS)[number];
type SectionTab = Exclude<AdminTab, ModelTab>;
type PrimaryKey = 'models' | SectionTab;

const MODEL_TABS: TabItem<ModelTab>[] = [
  { key: 'store', label: 'Stores' },
  { key: 'storeGroups', label: 'Store Groups' },
  { key: 'user', label: 'Users' },
  { key: 'assistant', label: 'Agents' },
  { key: 'adminOrders', label: 'Orders' },
  { key: 'adminCalls', label: 'Calls' },
  { key: 'adminReservations', label: 'Reservations' },
  { key: 'edit', label: 'Edit Store Info' },
];

const MODEL_KEYS = new Set<AdminTab>(MODEL_TAB_KEYS);

const PRIMARY_TABS: TabItem<PrimaryKey>[] = [
  { key: 'models', label: 'Models' },
  { key: 'insights', label: 'Insights' },
  { key: 'refundReview', label: 'Refund Review' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'knowledgeBases', label: 'Knowledge Bases' },
  { key: 'faqSupport', label: 'FAQ Support' },
  { key: 'posConfig', label: 'POS Config' },
  { key: 'tsv', label: 'Twilio Config' },
  { key: 'onboardingBilling', label: 'Onboarding & Billing' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'vipContacts', label: 'VIP Contacts' },
  { key: 'demoLines', label: 'Demo Lines' },
];

const TWILIO_TABS: TabItem<TwilioConfigSubtab>[] = [
  { key: 'phoneNumbers', label: 'Phone Numbers' },
  { key: 'tollFree', label: 'TFV Verification' },
  { key: 'secondaryProfiles', label: 'Secondary Profiles' },
];

const ONBOARDING_TABS: TabItem<OnboardingBillingSubtab>[] = [
  { key: 'offers', label: 'Activation Offers' },
  { key: 'intakes', label: 'Onboarding Intakes' },
  { key: 'plans', label: 'Billing Plans' },
  { key: 'financials', label: 'Financials' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'status', label: 'Onboarding Status' },
];

const PLANS_TABS: TabItem<PlansSubTab>[] = [
  { key: 'catalog', label: 'Plans Catalog' },
  { key: 'override', label: 'Custom Pricing Overrides' },
];

const INTEGRATION_TABS: TabItem<IntegrationSubTab>[] = [
  { key: 'manual', label: 'Manual Integrations' },
];

const POS_TABS: TabItem<PosConfigSubtab>[] = [
  { key: 'poshub', label: 'POSHUB' },
];

const NOTIFICATION_TABS: TabItem<NotificationSubTab>[] = [
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'sent', label: 'Sent' },
];

export const isModelAdminTab = (tab: AdminTab): tab is ModelTab =>
  MODEL_KEYS.has(tab);

function TabRow<K extends string>({
  label,
  items,
  value,
  onChange,
  size = 'md',
  spacing,
  nested,
}: {
  label: string;
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  size?: 'sm' | 'md';
  /**
   * Primary sections spread across the bar. Nested subheaders stay packed
   * on the left, the same way the Notifications row does.
   */
  spacing?: 'compact' | 'relaxed';
  nested?: boolean;
}) {
  const resolvedSpacing = spacing ?? (nested ? 'compact' : 'relaxed');
  return (
    <div
      className={clsx(
        'overflow-x-auto px-3 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        nested && 'border-t border-v2-line bg-v2-sunken/70',
      )}
    >
      <Tabs
        aria-label={label}
        size={size}
        spacing={resolvedSpacing}
        value={value}
        onChange={onChange}
        items={items}
        className="w-max min-w-full"
      />
    </div>
  );
}

export default function PlatformAdminNav({
  activeTab,
  onTab,
  onboardingSubtab,
  onOnboardingSubtab,
  plansSubtab,
  onPlansSubtab,
  twilioSubtab,
  onTwilioSubtab,
  integrationSubtab,
  onIntegrationSubtab,
  posSubtab,
  onPosSubtab,
  notificationsSubtab,
  onNotificationsSubtab,
}: {
  activeTab: AdminTab;
  onTab: (tab: AdminTab) => void;
  onboardingSubtab: OnboardingBillingSubtab;
  onOnboardingSubtab: (subtab: OnboardingBillingSubtab) => void;
  plansSubtab: PlansSubTab;
  onPlansSubtab: (subtab: PlansSubTab) => void;
  twilioSubtab: TwilioConfigSubtab;
  onTwilioSubtab: (subtab: TwilioConfigSubtab) => void;
  integrationSubtab: IntegrationSubTab;
  onIntegrationSubtab: (subtab: IntegrationSubTab) => void;
  posSubtab: PosConfigSubtab;
  onPosSubtab: (subtab: PosConfigSubtab) => void;
  notificationsSubtab: NotificationSubTab;
  onNotificationsSubtab: (subtab: NotificationSubTab) => void;
}) {
  const onModels = isModelAdminTab(activeTab);
  const primaryValue: PrimaryKey = onModels ? 'models' : activeTab;

  return (
    <div className="av2 overflow-hidden rounded-v2 border border-v2-line bg-v2-surface">
      <TabRow
        label="Platform admin sections"
        items={PRIMARY_TABS}
        value={primaryValue}
        onChange={(key) => {
          if (key === 'models') {
            if (!onModels) onTab('store');
            return;
          }
          onTab(key);
        }}
      />
      {onModels && (
        <TabRow
          nested
          size="sm"
          label="Models"
          items={MODEL_TABS}
          value={activeTab}
          onChange={onTab}
        />
      )}
      {activeTab === 'tsv' && (
        <TabRow
          nested
          size="sm"
          label="Twilio config"
          items={TWILIO_TABS}
          value={twilioSubtab}
          onChange={onTwilioSubtab}
        />
      )}
      {activeTab === 'onboardingBilling' && (
        <TabRow
          nested
          size="sm"
          label="Onboarding and billing"
          items={ONBOARDING_TABS}
          value={onboardingSubtab}
          onChange={onOnboardingSubtab}
        />
      )}
      {activeTab === 'onboardingBilling' && onboardingSubtab === 'plans' && (
        <TabRow
          nested
          size="sm"
          label="Billing plans"
          items={PLANS_TABS}
          value={plansSubtab}
          onChange={onPlansSubtab}
        />
      )}
      {activeTab === 'integrations' && (
        <TabRow
          nested
          size="sm"
          label="Integrations"
          items={INTEGRATION_TABS}
          value={integrationSubtab}
          onChange={onIntegrationSubtab}
        />
      )}
      {activeTab === 'notifications' && (
        <TabRow
          nested
          size="sm"
          label="Notifications"
          items={NOTIFICATION_TABS}
          value={notificationsSubtab}
          onChange={onNotificationsSubtab}
        />
      )}
      {activeTab === 'posConfig' && (
        <TabRow
          nested
          size="sm"
          label="POS config"
          items={POS_TABS}
          value={posSubtab}
          onChange={onPosSubtab}
        />
      )}
    </div>
  );
}
